import { CalendarOutlined, InfoCircleOutlined, LockOutlined } from '@ant-design/icons';
import { Card, Tag, Tooltip, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { WorkflowSummary } from '@/api/workflows';
import { type DisplayStatus, statusColor } from '@/originator/statusUtils';
import { assigneeOf, workflowCardTitle } from '@/utils/workflowHelpers';

import { VersionChip } from './VersionChip';

const { Text, Paragraph } = Typography;

// Every card in a row must be the same height, so the two rows whose content is
// optional reserve their space instead of collapsing. These drive both the clamp
// and the reserved height — keep them in sync by construction, not by hand.
const DESCRIPTION_ROWS = 2;
const DESCRIPTION_FONT_SIZE = 13;
const DESCRIPTION_LINE_HEIGHT = 1.5;
/** Height of the submitted-on line; reserved even when the date is absent. */
const SUBMITTED_ROW_HEIGHT = 18;

type Props = {
  /** Summary, not the full template — the card renders metadata only, so it works
   *  off the slim list payload as well as a fully-hydrated template. */
  workflow: WorkflowSummary;
  /** Label shown in the version chip (e.g. "v1.2"). */
  versionLabel: string;
  /** Status shown in the right-of-appName status tag. Accepts the BE statuses
   *  plus the derived PendingPublishing / Published states the Originator dashboard
   *  splits Approved into. */
  status: DisplayStatus;
  /** Action row contents. Consumer renders whichever icon buttons / toggles apply. */
  actions: ReactNode;
  /** Optional click handler on the status tag — used by the Originator's Rejected flow. */
  onStatusClick?: () => void;
  /** Style tweak from outside (e.g. a drag-handle wrapper sets cursor). */
  style?: React.CSSProperties;
};

/**
 * Shared workflow card body. Owns the header + sub-row + divider + assignee +
 * description layout; the action row is a slot so the Originator dashboard and
 * the Approver dashboard can pass their own action sets without forking the
 * card design.
 */
export function WorkflowCard({
  workflow: w,
  versionLabel,
  status,
  actions,
  onStatusClick,
  style,
}: Props) {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language.startsWith('fr');
  // Shared with the dashboard's A → Z sort and the action buttons' accessible
  // names — see workflowCardTitle. Keep it that way: a title derived here would
  // drift from the value the list sorts on.
  const title = workflowCardTitle(w, isFr);
  // Deliberately NOT localizedDescription: that falls back to the English text
  // when the French one is empty, and this card shows the em-dash placeholder
  // instead. Changing that is a content decision, not a sorting fix.
  const description = isFr ? w.descriptionFr : w.descriptionEn;
  const lockedByOther = !!w.lock && !w.lock.heldByCaller;
  // Request type — "New Form" for the workflow's very first submission cycle
  // (major version 1), "Change" for every subsequent version. Parsed off the
  // label shown on THIS card so a single workflow's Published (v1) card reads
  // as "New Form" while its in-flight v2 card correctly reads as "Change".
  const requestType = deriveRequestType(versionLabel);
  // Submitted date — only shown when the template has ever been submitted.
  // Localized to a compact date string; the exact ISO stays in the tooltip so
  // reviewers can hover to see the precise timestamp.
  const submittedDate = w.submittedAt ? new Date(w.submittedAt) : null;
  const submittedShort = submittedDate?.toLocaleDateString(i18n.language, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  const submittedFull = submittedDate?.toLocaleString(i18n.language);

  return (
    <Card
      hoverable
      // Fills its grid cell so every card in a row is the same height. Without
      // this the card keeps its natural height and cards whose optional rows are
      // absent (no description, never submitted) render visibly shorter than
      // their neighbours. The grid already stretches the cell; the card has to
      // opt into filling it, and the body has to be the flex child that grows.
      style={{
        borderRadius: 12,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...style,
      }}
      styles={{
        body: { padding: 18, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 },
      }}
    >
      {/* Header: title + lock chip + version chip */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 8,
        }}
      >
        {/* Clamped to two lines and always two lines tall. A one-line title next
            to a wrapping one is the other way cards in a grid end up different
            heights, and a three-line title would push its whole row taller than
            every other row. The full name stays in the DOM (and in the tooltip)
            so nothing is lost when it is visually truncated. */}
        <Tooltip title={title}>
          <Text strong className="workflow-card__title" style={{ fontStyle: 'normal' }}>
            {title}
          </Text>
        </Tooltip>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {/* Lock chip — only when SOMEONE ELSE currently holds it. BE drops expired
              locks from the response so any non-null `lock` here is a live hold. */}
          {lockedByOther && w.lock && (
            <Tooltip
              title={t('workflow.lock.bannerExpiry', {
                time: new Date(w.lock.expiresAt).toLocaleString(i18n.language),
              })}
            >
              <Tag
                icon={<LockOutlined />}
                color="orange"
                style={{ marginInlineEnd: 0, fontWeight: 500 }}
              >
                {t('workflow.lock.cardChip', {
                  name:
                    (w.lock.holderName ?? null)?.split(' ')[0] ?? t('workflow.lock.anotherUser'),
                })}
              </Tag>
            </Tooltip>
          )}
          <VersionChip label={versionLabel} />
        </div>
      </div>

      {/* AppName row — pill on its own line, full width. */}
      <div style={{ display: 'flex', alignItems: 'center', marginTop: 10 }}>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flex: 1,
            minWidth: 0,
            background: '#f3f6f9',
            border: '1px solid #e3e8ee',
            borderRadius: 8,
            padding: '4px 12px',
            fontSize: 13,
            fontFamily: "'SF Mono', ui-monospace, Menlo, Consolas, monospace",
            color: '#3a4250',
          }}
        >
          <span
            style={{
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {w.appName}
          </span>
        </span>
      </div>

      {/* Badges row — Request Type + (optional) clickable Status tag. Status
          is redundant on the dashboards today because each tab already scopes
          to a single status (Drafts / Waiting for approval / Approved /
          Rejected / Published), so we only render it when the caller wires
          `onStatusClick` — the Rejected flow uses that hook to pop the
          rejection-reason modal, so the tag stays as its click target. When
          no click handler is wired, the request-type tag stretches full
          width and status disappears from the card. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          gap: 8,
          marginTop: 8,
        }}
      >
        {/* Request type — New Form / Change. See deriveRequestType — the
            badge is derived from THIS card's version label so a v1 Published
            card reads as "New Form" while the same workflow's v2 in-flight
            card reads as "Change". Fills the row when status is hidden;
            half when the status click-target is shown. */}
        <Tag
          color={requestType === 'new' ? 'blue' : 'gold'}
          style={{
            marginInlineEnd: 0,
            flex: 1,
            minWidth: 0,
            textAlign: 'center',
            padding: '4px 10px',
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: 0.2,
          }}
        >
          {requestType === 'new'
            ? t('workflow.list.requestTypeNewForm', { defaultValue: 'New Form' })
            : t('workflow.list.requestTypeChange', { defaultValue: 'Change' })}
        </Tag>
        {onStatusClick && (
          // Clickable status — used by the Rejected card to pop the
          // rejection-reason modal. Underline + info icon signal that the
          // tag opens more info; tooltip names the action for keyboard /
          // screen-reader users.
          <Tooltip
            title={t('workflow.list.viewRejectionReason', {
              defaultValue: 'View rejection reason',
            })}
          >
            <Tag
              color={statusColor(status)}
              className="workflow-card__status-tag--clickable"
              style={{
                marginInlineEnd: 0,
                flex: 1,
                minWidth: 0,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                padding: '4px 10px',
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: 0.2,
                textDecoration: 'underline',
                textDecorationStyle: 'dotted',
                textUnderlineOffset: 3,
              }}
              onClick={onStatusClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onStatusClick();
                }
              }}
            >
              {t(`workflow.statusLabels.${status}`)}
              <InfoCircleOutlined style={{ fontSize: 11 }} />
            </Tag>
          </Tooltip>
        )}
      </div>

      <div style={{ height: 1, background: '#f0f0f0', margin: '14px 0' }} />

      {/* People block — Assignee (advisor target) + Created by (originator
          snapshot). Compact two-line stack; `Created by` only renders when
          we actually know the value so legacy rows without ownerName don't
          show an ugly oid fallback line unless we HAVE to. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Text style={{ fontSize: 14, fontStyle: 'normal', color: '#1f2937' }}>
          <Text strong style={{ fontStyle: 'normal', color: '#111827', fontSize: 14 }}>
            {t('workflow.list.assignee')}:
          </Text>{' '}
          {assigneeOf(w, t)}
        </Text>
        {(w.ownerName?.trim() || w.ownerOid) && (
          <Text style={{ fontSize: 14, fontStyle: 'normal', color: '#1f2937' }}>
            <Text strong style={{ fontStyle: 'normal', color: '#111827', fontSize: 14 }}>
              {t('workflow.list.createdBy', { defaultValue: 'Created by' })}:
            </Text>{' '}
            {w.ownerName?.trim() || w.ownerOid}
          </Text>
        )}
      </div>

      {/* Always two lines tall, whether the description is absent, one line, or
          clamped at two — so the rows below it start at the same y on every
          card. minHeight must match `rows` x fontSize x lineHeight exactly;
          a smaller value lets a genuine two-line description push the card. */}
      <Paragraph
        ellipsis={{ rows: DESCRIPTION_ROWS }}
        style={{
          fontSize: DESCRIPTION_FONT_SIZE,
          lineHeight: DESCRIPTION_LINE_HEIGHT,
          marginTop: 8,
          marginBottom: 0,
          minHeight: DESCRIPTION_ROWS * DESCRIPTION_FONT_SIZE * DESCRIPTION_LINE_HEIGHT,
          fontStyle: 'normal',
          color: '#4b5563',
        }}
      >
        {description || '—'}
      </Paragraph>

      {/* Submitted date — subtle inline hint tucked under the description
          so it doesn't compete with the tags on the identifier row. Muted
          text + calendar icon; full timestamp lives in the tooltip. Drafts
          that have never been submitted have nothing to show, but the row
          still reserves its height so the action row below lines up with the
          neighbouring cards instead of riding up. */}
      <div style={{ marginTop: 8, height: SUBMITTED_ROW_HEIGHT }}>
        {submittedShort && (
          <Tooltip title={submittedFull}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: '100%',
                fontSize: 12,
                color: '#6b7280',
              }}
            >
              <CalendarOutlined style={{ fontSize: 11 }} />
              <span>
                {t('workflow.list.submittedOnShort', {
                  date: submittedShort,
                  defaultValue: 'Submitted {{date}}',
                })}
              </span>
            </div>
          </Tooltip>
        )}
      </div>

      {/* Absorbs the slack from being stretched to the tallest card in the row,
          pushing the action row to the bottom so the icon rows line up across
          the grid. A spacer rather than `marginTop: auto` on the row below,
          because auto collapses to 0 on the tallest card and would eat that
          row's 14px gap — the shortest and tallest cards would then space
          their buttons differently. */}
      <div style={{ flex: 1, minHeight: 14 }} />

      <div
        style={{
          display: 'flex',
          gap: 8,
          paddingTop: 14,
          borderTop: '1px solid #f0f0f0',
          alignItems: 'center',
        }}
      >
        {actions}
      </div>
    </Card>
  );
}

/**
 * Read the workflow's request type from the version label shown on this card.
 * Major version 1 (labels `v1` or `v1.<n>`) is the workflow's very first
 * submission cycle — the originator is authoring / awaiting approval on a
 * brand-new smart form. Any higher major is a change to an already-approved
 * form.
 *
 * Empty / unparseable labels default to `new` — the safest fallback for a
 * fresh draft that hasn't been labelled yet. Parsing the label rather than the
 * template's `currentVersionNumber` matters because a single workflow's
 * Published card and its in-flight v2 card resolve differently: the published
 * v1 reads as New Form even while a v2 change is being drafted.
 */
function deriveRequestType(versionLabel: string): 'new' | 'change' {
  const match = /^v(\d+)/i.exec(versionLabel.trim());
  if (!match) return 'new';
  return Number(match[1]) <= 1 ? 'new' : 'change';
}
